import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { fetchAuthSession } from 'aws-amplify/auth';
import { environment } from '../../environments/environment';
import {
  ChatRequestDto,
  ChatSseEvent,
  StartChatRequest,
  StartModificationChatRequest,
} from '../models/rule-creation-chat.model';

@Injectable({
  providedIn: 'root',
})
export class RuleCreationChatService {
  private readonly baseUrl = environment.gatewayUrl;

  private async getAuthHeader(): Promise<string> {
    const session = await fetchAuthSession();
    return `Bearer ${session.tokens?.idToken?.toString()}`;
  }

  startSession(functionalLocationId: string): Observable<ChatSseEvent> {
    const request: StartChatRequest = { functionalLocationId };
    return this.streamPost('RuleCreationChat/Start/', request);
  }

  startModificationSession(ruleId: string): Observable<ChatSseEvent> {
    const request: StartModificationChatRequest = { ruleId };
    return this.streamPost('RuleModificationChat/Start/', request);
  }

  streamMessage(request: ChatRequestDto): Observable<ChatSseEvent> {
    return this.streamPost('RuleCreationChat/Message/', request);
  }

  streamModificationMessage(request: ChatRequestDto): Observable<ChatSseEvent> {
    return this.streamPost('RuleModificationChat/Message/', request);
  }

  private streamPost(path: string, body: unknown): Observable<ChatSseEvent> {
    return new Observable<ChatSseEvent>((observer) => {
      let abortController = new AbortController();

      const run = async () => {
        try {
          const authHeader = await this.getAuthHeader();
          const response = await fetch(`${this.baseUrl}/${path}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
            },
            body: JSON.stringify(body),
            signal: abortController.signal,
          });

          if (!response.ok) {
            observer.error(
              new Error(`HTTP ${response.status}: ${response.statusText}`),
            );
            return;
          }

          const reader = response.body!.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() ?? '';

            for (const part of parts) {
              const event = this.parseSseBlock(part);
              if (event) {
                observer.next(event);
              }
            }
          }

          observer.complete();
        } catch (err: any) {
          if (err?.name !== 'AbortError') {
            observer.error(err);
          }
        }
      };

      run();

      return () => abortController.abort();
    });
  }

  private parseSseBlock(block: string): ChatSseEvent | null {
    let dataLine: string | undefined;
    for (const line of block.split('\n')) {
      if (line.startsWith('data: ')) {
        dataLine = line.slice(6);
      }
    }
    if (!dataLine) return null;
    try {
      return JSON.parse(dataLine) as ChatSseEvent;
    } catch {
      return null;
    }
  }
}
